import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { FormField } from '@/components/ui/form-field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import type { FloatingBadge, HeroSlide, HeroSlideFormData, HeroStat } from '@/types/api'
import { zodResolver } from '@hookform/resolvers/zod'
import { Plus, Trash2 } from 'lucide-react'
import React from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { z } from 'zod'

const floatingBadgeSchema = z.object({
  icon: z.string().min(1, 'Icon is required'),
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  position: z.enum(['top-right', 'bottom-left', 'top-left', 'bottom-right']),
})

const heroStatSchema = z.object({
  value: z.string().min(1, 'Value is required'),
  label: z.string().min(1, 'Label is required'),
})

const heroSlideFormSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  title_highlight: z.string().optional(),
  subtitle: z.string().optional(),
  primary_cta_text: z.string().optional(),
  primary_cta_link: z.string().optional(),
  secondary_cta_text: z.string().optional(),
  secondary_cta_link: z.string().optional(),
  is_active: z.boolean(),
  order: z.number().min(0),
  floating_badges: z.array(floatingBadgeSchema).optional(),
  stats: z.array(heroStatSchema).optional(),
})

type FormValues = z.infer<typeof heroSlideFormSchema>

interface HeroSlideFormProps {
  className?: string
  initialData?: HeroSlide
  onSubmit: (_data: HeroSlideFormData) => void
  onCancel: () => void
  isLoading?: boolean
}

export const HeroSlideForm: React.FC<HeroSlideFormProps> = ({
  className,
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(heroSlideFormSchema),
    defaultValues: {
      title: initialData?.title || '',
      title_highlight: initialData?.title_highlight || '',
      subtitle: initialData?.subtitle || '',
      primary_cta_text: initialData?.primary_cta_text || '',
      primary_cta_link: initialData?.primary_cta_link || '',
      secondary_cta_text: initialData?.secondary_cta_text || '',
      secondary_cta_link: initialData?.secondary_cta_link || '',
      is_active: initialData?.is_active ?? true,
      order: initialData?.order ?? 0,
      floating_badges: (initialData?.floating_badges as FloatingBadge[]) || [],
      stats: (initialData?.stats as HeroStat[]) || [],
    },
  })

  const {
    fields: badgeFields,
    append: appendBadge,
    remove: removeBadge,
  } = useFieldArray({
    control,
    name: 'floating_badges',
  })

  const {
    fields: statFields,
    append: appendStat,
    remove: removeStat,
  } = useFieldArray({
    control,
    name: 'stats',
  })

  const [imageFile, setImageFile] = React.useState<File | null>(null)

  const onFormSubmit = (data: FormValues) => {
    onSubmit({
      title: data.title,
      title_highlight: data.title_highlight,
      subtitle: data.subtitle,
      primary_cta_text: data.primary_cta_text,
      primary_cta_link: data.primary_cta_link,
      secondary_cta_text: data.secondary_cta_text,
      secondary_cta_link: data.secondary_cta_link,
      is_active: data.is_active,
      order: data.order,
      floating_badges: data.floating_badges,
      stats: data.stats,
      background_image: imageFile || undefined,
    })
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>{initialData ? 'Edit Hero Slide' : 'Add Hero Slide'}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="font-medium text-lg">Basic Information</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField id="title" label="Title" error={!!errors.title} required>
                <Input {...register('title')} placeholder="Premium Water Pumps for" />
              </FormField>

              <FormField id="title_highlight" label="Title Highlight (colored text)">
                <Input {...register('title_highlight')} placeholder="Every Need" />
              </FormField>
            </div>

            <FormField id="subtitle" label="Subtitle">
              <Textarea
                {...register('subtitle')}
                placeholder="Discover our comprehensive range of high-quality water pumps..."
                rows={3}
              />
            </FormField>

            <FormField id="background_image" label="Background Image" required={!initialData}>
              <div className="space-y-2">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setImageFile(e.target.files[0])
                    }
                  }}
                />
                {initialData?.background_image && !imageFile && (
                  <div className="text-sm text-muted-foreground">
                    Current image:{' '}
                    <img
                      src={initialData.background_image}
                      alt="Current background"
                      className="mt-2 max-h-32 rounded border object-cover"
                    />
                  </div>
                )}
                {imageFile && (
                  <div className="text-sm text-muted-foreground">New image: {imageFile.name}</div>
                )}
              </div>
            </FormField>
          </div>

          {/* CTA Buttons */}
          <div className="space-y-4">
            <h3 className="font-medium text-lg">Call to Action Buttons</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField id="primary_cta_text" label="Primary Button Text">
                <Input {...register('primary_cta_text')} placeholder="Shop Now" />
              </FormField>
              <FormField id="primary_cta_link" label="Primary Button Link">
                <Input {...register('primary_cta_link')} placeholder="/products" />
              </FormField>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField id="secondary_cta_text" label="Secondary Button Text">
                <Input {...register('secondary_cta_text')} placeholder="Watch Demo" />
              </FormField>
              <FormField id="secondary_cta_link" label="Secondary Button Link">
                <Input {...register('secondary_cta_link')} placeholder="#demo" />
              </FormField>
            </div>
          </div>

          {/* Floating Badges */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-lg">Floating Badges</h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  appendBadge({ icon: 'Zap', title: '', description: '', position: 'top-right' })
                }
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Badge
              </Button>
            </div>

            {badgeFields.map((field, index) => (
              <div key={field.id} className="grid grid-cols-5 gap-2 items-end border p-3 rounded">
                <FormField id={`badge-icon-${index}`} label="Icon">
                  <Input {...register(`floating_badges.${index}.icon`)} placeholder="Zap" />
                </FormField>
                <FormField id={`badge-title-${index}`} label="Title">
                  <Input
                    {...register(`floating_badges.${index}.title`)}
                    placeholder="High Efficiency"
                  />
                </FormField>
                <FormField id={`badge-desc-${index}`} label="Description">
                  <Input
                    {...register(`floating_badges.${index}.description`)}
                    placeholder="Up to 95%"
                  />
                </FormField>
                <FormField id={`badge-position-${index}`} label="Position">
                  <select
                    {...register(`floating_badges.${index}.position`)}
                    className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="top-right">Top Right</option>
                    <option value="top-left">Top Left</option>
                    <option value="bottom-right">Bottom Right</option>
                    <option value="bottom-left">Bottom Left</option>
                  </select>
                </FormField>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeBadge(index)}
                  className="text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>

          {/* Stats */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-lg">Stats</h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => appendStat({ value: '', label: '' })}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Stat
              </Button>
            </div>

            {statFields.map((field, index) => (
              <div key={field.id} className="grid grid-cols-3 gap-2 items-end border p-3 rounded">
                <FormField id={`stat-value-${index}`} label="Value">
                  <Input {...register(`stats.${index}.value`)} placeholder="25+" />
                </FormField>
                <FormField id={`stat-label-${index}`} label="Label">
                  <Input {...register(`stats.${index}.label`)} placeholder="Years Experience" />
                </FormField>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeStat(index)}
                  className="text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>

          {/* Settings */}
          <div className="space-y-4">
            <h3 className="font-medium text-lg">Settings</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField id="order" label="Display Order">
                <Input type="number" {...register('order', { valueAsNumber: true })} />
              </FormField>

              <FormField id="is_active" label="Status">
                <div className="flex items-center space-x-2 h-10">
                  <input
                    type="checkbox"
                    id="is_active_check"
                    {...register('is_active')}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <label htmlFor="is_active_check" className="text-sm font-medium text-gray-700">
                    Active
                  </label>
                </div>
              </FormField>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : initialData ? 'Update Slide' : 'Create Slide'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
